import { lazy, Suspense, Component } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { ThemeEngine } from './design-system';
import { ToastProvider } from './design-system';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { OrgSettingsProvider } from './context/OrgSettingsContext';
import { ProtectedRoute } from './utils/roleGuard';
import { isHrDepartmentUser } from './utils/roles';
import { isNativePlatform } from './mobile/platform';

// Mobile app — loaded only on Capacitor
const MobileApp = lazy(() => import('./mobile/MobileApp'));

// ── Error Boundary for lazy-loaded pages ──────────────────
class ChunkErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('[ChunkErrorBoundary]', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 2 }}>
          <Typography variant="h5" fontWeight={600}>Something went wrong</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, textAlign: 'center' }}>
            {this.state.error?.message || 'An unexpected error occurred while loading this page.'}
          </Typography>
          <Button variant="contained" onClick={() => window.location.reload()}>Reload Page</Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

// ── Lazy page imports ─────────────────────────────────────
const Layout            = lazy(() => import('./components/Layout'));
const Login             = lazy(() => import('./pages/Login'));
const Register          = lazy(() => import('./pages/RegisterEmployee'));
const ForgotPassword    = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword     = lazy(() => import('./pages/ResetPassword'));
const AcceptInvite      = lazy(() => import('./pages/AcceptInvite'));
const HomeDashboard     = lazy(() => import('./pages/HomeDashboard'));
const Dashboard         = lazy(() => import('./pages/Dashboard'));
const EngineerDashboard = lazy(() => import('./pages/EngineerDashboard'));
const ManagerDashboard  = lazy(() => import('./pages/ManagerDashboard'));
const EmployeeDashboard = lazy(() => import('./pages/EmployeeDashboard'));
const Tickets           = lazy(() => import('./pages/Tickets'));
const MyTasks           = lazy(() => import('./pages/MyTasks'));
const TicketWorkspace   = lazy(() => import('./pages/TicketWorkspace'));
const TicketPool        = lazy(() => import('./pages/TicketPool'));
const Assets            = lazy(() => import('./pages/Assets'));
const AssetManagementDashboard = lazy(() => import('./pages/AssetManagementDashboard'));
const AssetReports             = lazy(() => import('./pages/AssetReports'));
const Users             = lazy(() => import('./pages/Users'));
const Vendors           = lazy(() => import('./pages/Vendors'));
const Maintenance       = lazy(() => import('./pages/Maintenance'));
const MaintenanceMgmtDashboard = lazy(() => import('./pages/maintenance/MaintenanceDashboard'));
const MaintenanceMgmtList      = lazy(() => import('./pages/maintenance/MaintenanceList'));
const MaintenanceMgmtForm      = lazy(() => import('./pages/maintenance/MaintenanceForm'));
const MaintenanceMgmtDetail    = lazy(() => import('./pages/maintenance/MaintenanceDetail'));
const MaintenanceMgmtCalendar  = lazy(() => import('./pages/maintenance/MaintenanceCalendar'));
const MaintenanceMgmtReports   = lazy(() => import('./pages/maintenance/MaintenanceReports'));
const MaintenanceMgmtWorkOrder = lazy(() => import('./pages/maintenance/MaintenanceWorkOrder'));
const Backups           = lazy(() => import('./pages/Backups'));
const Reports           = lazy(() => import('./pages/Reports'));
const EngineerReport    = lazy(() => import('./pages/EngineerReport'));
const EngineerPerformance = lazy(() => import('./pages/EngineerPerformance'));
const ServerRequests    = lazy(() => import('./pages/ServerRequests'));
const VpnRequests       = lazy(() => import('./pages/VpnRequests'));
const AuditLogs         = lazy(() => import('./pages/AuditLogs'));
const Departments       = lazy(() => import('./pages/Departments'));
const Contacts          = lazy(() => import('./pages/Contacts'));
const Roles             = lazy(() => import('./pages/Roles'));
const SmtpSettings      = lazy(() => import('./pages/SmtpSettings'));
const SLASettings       = lazy(() => import('./pages/SLASettings'));
const EmailPreview      = lazy(() => import('./pages/EmailPreview'));
const InviteUser             = lazy(() => import('./pages/InviteUser'));
const EmployeeWelcomeKit     = lazy(() => import('./pages/EmployeeWelcomeKit'));
const WelcomeKitAcceptance   = lazy(() => import('./pages/WelcomeKitAcceptance'));
const Notifications     = lazy(() => import('./pages/Notifications'));
const Profile           = lazy(() => import('./pages/Profile'));
const Help              = lazy(() => import('./pages/Help'));
const ReleaseNotes      = lazy(() => import('./pages/ReleaseNotes'));
// Problem Management
const Problems          = lazy(() => import('./pages/Problems'));
const ProblemDetail     = lazy(() => import('./pages/ProblemDetail'));
const ProblemDashboard  = lazy(() => import('./pages/ProblemDashboard'));
const KnownErrors       = lazy(() => import('./pages/KnownErrors'));
// Knowledge Base
const KnowledgeDashboard  = lazy(() => import('./pages/KnowledgeDashboard'));
const KnowledgeList       = lazy(() => import('./pages/KnowledgeList'));
const KnowledgeDetail     = lazy(() => import('./pages/KnowledgeDetail'));
const KnowledgeEditor     = lazy(() => import('./pages/KnowledgeEditor'));
const KnowledgeApproval   = lazy(() => import('./pages/KnowledgeApproval'));
const KnowledgeCategories = lazy(() => import('./pages/KnowledgeCategories'));
// CMDB
const CmdbDashboard         = lazy(() => import('./pages/CmdbDashboard'));
const CmdbList              = lazy(() => import('./pages/CmdbList'));
const CmdbDetail            = lazy(() => import('./pages/CmdbDetail'));
const CmdbForm              = lazy(() => import('./pages/CmdbForm'));
const CmdbReports           = lazy(() => import('./pages/CmdbReports'));
const CmdbPhase2Dashboard   = lazy(() => import('./pages/CmdbPhase2Dashboard'));
const CmdbAdmin             = lazy(() => import('./pages/CmdbAdmin'));
const CmdbCreateWizard      = lazy(() => import('./pages/CmdbCreateWizard'));
const CmdbEnterpriseDashboard = lazy(() => import('./pages/CmdbEnterpriseDashboard'));
const CmdbDependencyExplorer = lazy(() => import('./pages/CmdbDependencyExplorer'));
const CmdbGovernanceDashboard = lazy(() => import('./pages/CmdbGovernanceDashboard'));
const CmdbSyncDashboard     = lazy(() => import('./pages/CmdbSyncDashboard'));
const CmdbTopologyViewer    = lazy(() => import('./pages/CmdbTopologyViewer'));
const CmdbCIWorkspace       = lazy(() => import('./pages/CmdbCIWorkspace'));
const CmdbOperationsCenter  = lazy(() => import('./pages/CmdbOperationsCenter'));
const BusinessServices      = lazy(() => import('./pages/BusinessServices'));
const BusinessServiceDetail = lazy(() => import('./pages/BusinessServiceDetail'));
const BusinessServiceForm   = lazy(() => import('./pages/BusinessServiceForm'));
const CIRelationships       = lazy(() => import('./pages/CIRelationships'));
const ImpactAnalysis        = lazy(() => import('./pages/ImpactAnalysis'));
// Administration
const SystemHealthDashboard = lazy(() => import('./pages/SystemHealthDashboard'));
const OrgSettingsPage       = lazy(() => import('./pages/OrgSettings'));
const ThemeSettings         = lazy(() => import('./pages/ThemeSettings'));
const TicketCategories      = lazy(() => import('./pages/TicketCategories'));
const VendorServiceTypes    = lazy(() => import('./pages/VendorServiceTypes'));
const AssetCategorySettings = lazy(() => import('./pages/AssetCategorySettings'));
const OEMSettings           = lazy(() => import('./pages/OEMSettings'));
const Projects              = lazy(() => import('./pages/Projects'));
// Hardware Request Management
const HardwareRequests        = lazy(() => import('./pages/HardwareRequests'));
const HardwareRequestDetail   = lazy(() => import('./pages/HardwareRequestDetail'));
const HardwareAcceptanceForm  = lazy(() => import('./pages/HardwareAcceptanceForm'));
const MyAssetForms           = lazy(() => import('./pages/MyAssetForms'));
const AssetReturnOnBehalf    = lazy(() => import('./pages/AssetReturnOnBehalf'));
const HardwareFulfilmentQueue = lazy(() => import('./pages/HardwareFulfilmentQueue'));
const HardwareDashboard       = lazy(() => import('./pages/HardwareDashboard'));
const HardwareManagerApprovals = lazy(() => import('./pages/HardwareManagerApprovals'));
const ManagerPendingApprovals  = lazy(() => import('./pages/ManagerPendingApprovals'));
const HardwareProcurement     = lazy(() => import('./pages/HardwareProcurement'));
const HardwareReports         = lazy(() => import('./pages/HardwareReports'));
// Software Asset Management
const SoftwareCatalog         = lazy(() => import('./pages/SoftwareCatalog'));
const SoftwareRequests        = lazy(() => import('./pages/SoftwareRequests'));
const SoftwareLicenses        = lazy(() => import('./pages/SoftwareLicenses'));
const SoftwareDashboard       = lazy(() => import('./pages/SoftwareDashboard'));
const MySoftwareLicenses      = lazy(() => import('./pages/MySoftwareLicenses'));
// Vendor Governance
const VendorHub               = lazy(() => import('./pages/VendorHub'));
const VendorContracts         = lazy(() => import('./pages/VendorContracts'));
const VendorGovernanceDashboard = lazy(() => import('./pages/VendorGovernanceDashboard'));
// Vendor Lifecycle
const VendorLifecycleDashboard = lazy(() => import('./pages/VendorLifecycleDashboard'));
const VendorOnboarding        = lazy(() => import('./pages/VendorOnboarding'));
const VendorCertifications    = lazy(() => import('./pages/VendorCertifications'));
const VendorCommunications    = lazy(() => import('./pages/VendorCommunications'));
const VendorSLABreaches       = lazy(() => import('./pages/VendorSLABreaches'));
const VendorInvoices          = lazy(() => import('./pages/VendorInvoices'));
// Vendor Analytics (Phase 2 & 3)
const VendorSpendAnalysis     = lazy(() => import('./pages/VendorSpendAnalysis'));
const VendorComparison        = lazy(() => import('./pages/VendorComparison'));
const VendorRiskOverview      = lazy(() => import('./pages/VendorRiskOverview'));
const VendorSchedulerSettings = lazy(() => import('./pages/VendorSchedulerSettings'));
// Financial Governance
const FinanceDashboard        = lazy(() => import('./pages/FinanceDashboard'));
const BudgetManagement        = lazy(() => import('./pages/BudgetManagement'));
const ServiceCostTracking     = lazy(() => import('./pages/ICTExpenses'));
// Service Catalog
const ServicePortal           = lazy(() => import('./pages/ServicePortal'));
// Enterprise Location Management
const LocationDashboard       = lazy(() => import('./pages/LocationDashboard'));
const LocationTree            = lazy(() => import('./pages/LocationTree'));
const LocationList            = lazy(() => import('./pages/LocationList'));
const LocationDetail          = lazy(() => import('./pages/LocationDetail'));
const LocationReports         = lazy(() => import('./pages/LocationReports'));
const LocationImport          = lazy(() => import('./pages/LocationImport'));
const LocationSettings        = lazy(() => import('./pages/LocationSettings'));
// Enterprise Warehouse Management
const WarehouseDashboard      = lazy(() => import('./pages/WarehouseDashboard'));
const WarehouseList           = lazy(() => import('./pages/WarehouseList'));
const StockExplorer           = lazy(() => import('./pages/StockExplorer'));
const GoodsReceiptPage        = lazy(() => import('./pages/GoodsReceiptPage'));
const WarehouseOperations     = lazy(() => import('./pages/WarehouseOperations'));
const WarehouseReports        = lazy(() => import('./pages/WarehouseReports'));
const WarehouseImport         = lazy(() => import('./pages/WarehouseImport'));
// Knowledge Document Repository
const DocumentLibrary         = lazy(() => import('./pages/DocumentLibrary'));
const DocumentDashboard       = lazy(() => import('./pages/DocumentDashboard'));
// Enterprise Repair Management
const RepairDashboard         = lazy(() => import('./pages/RepairDashboard'));
// Employee Maintenance Schedule
const EmployeeMaintenanceSchedule = lazy(() => import('./pages/EmployeeMaintenanceSchedule'));
// Enterprise SAM
const SAMDashboard            = lazy(() => import('./pages/SAMDashboard'));
const SAMAssignments          = lazy(() => import('./pages/SAMAssignments'));
const SAMReports              = lazy(() => import('./pages/SAMReports'));
// Asset Workspace
const AssetWorkspace          = lazy(() => import('./pages/AssetWorkspace'));
// Asset Allocation Intelligence (Sprint 3)
const AllocationCenter        = lazy(() => import('./pages/AllocationCenter'));
const EmployeePortfolio       = lazy(() => import('./pages/EmployeePortfolio'));
const LoanCenter              = lazy(() => import('./pages/LoanCenter'));
const TransferCenter          = lazy(() => import('./pages/TransferCenter'));
const ReturnCenter            = lazy(() => import('./pages/ReturnCenter'));
const DamageRecovery          = lazy(() => import('./pages/DamageRecovery'));
const ReservationCenter       = lazy(() => import('./pages/ReservationCenter'));
const OperationsHub           = lazy(() => import('./pages/OperationsHub'));
const InventoryIntelligence   = lazy(() => import('./pages/InventoryIntelligence'));
const BulkOperations          = lazy(() => import('./pages/BulkOperations'));
const QRScanner               = lazy(() => import('./pages/QRScanner'));
// Infrastructure Deployment
const InfraDeployments        = lazy(() => import('./pages/InfraDeployments'));

// Asset Configuration & Component Management (Sprint 4)
const ConfigurationDashboard  = lazy(() => import('./pages/ConfigurationDashboard'));
const ComponentRegistry       = lazy(() => import('./pages/ComponentRegistry'));
const ComponentWorkspace      = lazy(() => import('./pages/ComponentWorkspace'));
const ConfigurationBaselines  = lazy(() => import('./pages/ConfigurationBaselines'));

// Asset Intelligence & Financial Management (Sprint 5)
const AssetIntelligenceDashboard = lazy(() => import('./pages/AssetIntelligenceDashboard'));
const FinancialWorkspace         = lazy(() => import('./pages/FinancialWorkspace'));
const AssetFinancialManagement   = lazy(() => import('./pages/FinancialWorkspace/AssetFinancialManagement'));
const BudgetForecastPage         = lazy(() => import('./pages/BudgetForecast'));
const ReplacementPlanner         = lazy(() => import('./pages/ReplacementPlanner'));
const VendorScorecards           = lazy(() => import('./pages/VendorScorecards'));

// Remote Desktop Session Management
const RemoteDesktopViewer        = lazy(() => import('./pages/RemoteDesktop/RemoteDesktopViewer'));
const SessionHistory             = lazy(() => import('./pages/RemoteDesktop/SessionHistory'));
const DownloadAgent              = lazy(() => import('./pages/RemoteDesktop/DownloadAgent'));
const WallpaperDeploy            = lazy(() => import('./pages/RemoteDesktop/WallpaperDeploy'));
const WallpaperSchedulePage      = lazy(() => import('./pages/RemoteDesktop/WallpaperSchedule'));
const AgentConfigPassword        = lazy(() => import('./pages/RemoteDesktop/AgentConfigPassword'));

// Service Renewal Management
const ServiceRenewals            = lazy(() => import('./pages/Reminders'));
const ContractAnalysis           = lazy(() => import('./pages/ContractAnalysis'));

// Banner Management
const BannerManagement           = lazy(() => import('./pages/BannerManagement'));

// Mobile App Download
const DownloadApp                = lazy(() => import('./pages/DownloadApp'));

// Mobile App Version Management
const AppVersionManagement       = lazy(() => import('./pages/AppVersionManagement'));
const MobileAppReport            = lazy(() => import('./pages/MobileAppReport'));

// ── Role groups ───────────────────────────────────────────
const ICT      = ['admin', 'ict_manager', 'dashboard_viewer'];
const STAFF    = ['admin', 'ict_manager', 'jr_engineer', 'sr_engineer'];
const ENG      = ['jr_engineer', 'sr_engineer'];
const MGMT     = ['admin', 'ict_manager', 'employee_manager', 'project_manager', 'delivery_manager'];
const USER_MGT = ['admin', 'ict_manager'];
const PROB_MGT = ['admin', 'ict_manager', 'sr_engineer', 'jr_engineer'];
const KM_WRITE = ['admin', 'ict_manager', 'sr_engineer', 'jr_engineer'];
const KM_APPROVAL = ['admin', 'ict_manager', 'sr_engineer'];
const CMDB_MGT = ['admin', 'ict_manager', 'sr_engineer'];
const CMDB_ALL = ['admin', 'ict_manager', 'sr_engineer', 'jr_engineer'];
const HW_ALL   = ['admin', 'ict_manager', 'sr_engineer', 'jr_engineer', 'employee_manager', 'project_manager', 'delivery_manager', 'employee'];
const HW_ICT   = ['admin', 'ict_manager', 'sr_engineer', 'jr_engineer'];
const SW_ALL   = ['admin', 'ict_manager', 'sr_engineer', 'jr_engineer', 'employee_manager', 'project_manager', 'delivery_manager', 'employee'];
const SW_ICT   = ['admin', 'ict_manager', 'sr_engineer', 'jr_engineer'];
const ALL      = ['admin', 'ict_manager', 'sr_engineer', 'jr_engineer', 'employee_manager', 'project_manager', 'delivery_manager', 'employee'];

// ── Page loader shown during lazy chunk fetch ─────────────
function PageLoader() {
  return (
    <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh"
      sx={{ background: '#f1f5f9', gap: 2 }}>
      <Box sx={{
        width: 44, height: 44, borderRadius: '12px',
        background: 'linear-gradient(135deg, #1a56db, #7c3aed)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 4px 16px rgba(26,86,219,0.3)',
      }}>
        <CircularProgress size={22} sx={{ color: 'white' }} thickness={3} />
      </Box>
      <Typography variant="caption" color="text.secondary" fontWeight={500}>Loading…</Typography>
    </Box>
  );
}

function RootRedirect() {
  const { homeRoute } = useAuth();
  return <Navigate to={homeRoute} replace />;
}

function Unauthorized() {
  const { homeRoute } = useAuth();
  return (
    <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center"
      minHeight="60vh" textAlign="center" px={3}>
      <Box sx={{
        width: 72, height: 72, borderRadius: '20px',
        background: 'linear-gradient(135deg, #fef2f2, #fee2e2)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        mb: 2.5, fontSize: 32,
      }}>
        🔒
      </Box>
      <Typography variant="h5" fontWeight={700} mb={1} color="text.primary">Access Denied</Typography>
      <Typography variant="body2" color="text.secondary" mb={3} maxWidth={320}>
        You don't have permission to view this page. Contact your administrator if you think this is a mistake.
      </Typography>
      <Button variant="contained" href={homeRoute} sx={{ borderRadius: 8 }}>
        Go to my home page
      </Button>
    </Box>
  );
}

export default function App() {
  // If running inside Capacitor (mobile APK), render mobile-specific app
  if (isNativePlatform()) {
    return (
      <Suspense fallback={<PageLoader />}>
        <MobileApp />
      </Suspense>
    );
  }

  // Web app — original code below
  return (
    <ThemeEngine>
      <ToastProvider>
      <AuthProvider>
        <NotificationProvider>
          <OrgSettingsProvider>
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <ChunkErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public */}
                <Route path="/login"           element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password"  element={<ResetPassword />} />
                <Route path="/accept-invite"   element={<AcceptInvite />} />

                {/* Protected shell */}
                <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                  <Route index element={<ProtectedRoute><RootRedirect /></ProtectedRoute>} />
                  <Route path="home" element={<HomeDashboard />} />

                  {/* ── Dashboard (legacy direct routes — redirect to /home) ── */}
                  <Route path="dashboard"
                    element={<ProtectedRoute roles={ICT}><Dashboard /></ProtectedRoute>} />
                  <Route path="my-dashboard"
                    element={<ProtectedRoute roles={ENG}><EngineerDashboard /></ProtectedRoute>} />
                  <Route path="my-tickets"
                    element={<ProtectedRoute roles={['employee', 'employee_manager', 'project_manager', 'delivery_manager']}><EmployeeDashboard /></ProtectedRoute>} />
                  <Route path="team-dashboard"
                    element={<ProtectedRoute roles={MGMT}><ManagerDashboard /></ProtectedRoute>} />
                  <Route path="pending-approvals"
                    element={<ProtectedRoute roles={MGMT}><ManagerPendingApprovals /></ProtectedRoute>} />

                  {/* ── Service Desk ── */}
                  <Route path="tickets"         element={<Tickets />} />
                  <Route path="tickets/:id"     element={<Tickets />} />
                  <Route path="tickets/:id/workspace" element={<TicketWorkspace />} />
                  <Route path="ticket-pool"
                    element={<ProtectedRoute roles={STAFF}><TicketPool /></ProtectedRoute>} />
                  <Route path="my-tasks"
                    element={<ProtectedRoute roles={STAFF}><MyTasks /></ProtectedRoute>} />
                  <Route path="server-requests" element={<ServerRequests />} />
                  <Route path="vpn-requests"    element={<VpnRequests />} />
                  <Route path="software-requests" element={<ProtectedRoute roles={SW_ALL}><SoftwareRequests /></ProtectedRoute>} />
                  <Route path="service-renewals"
                    element={<ProtectedRoute roles={ALL}><ServiceRenewals /></ProtectedRoute>} />
                  <Route path="contract-analysis"
                    element={<ProtectedRoute roles={STAFF}><ContractAnalysis /></ProtectedRoute>} />

                  {/* ── Hardware Request Management ── */}
                  <Route path="hardware/requests/new"
                    element={<Navigate to="/hardware/requests" replace />} />
                  <Route path="hardware/requests"
                    element={<ProtectedRoute roles={HW_ALL}><HardwareRequests /></ProtectedRoute>} />
                  <Route path="hardware/requests/:id"
                    element={<ProtectedRoute roles={HW_ALL}><HardwareRequestDetail /></ProtectedRoute>} />
                  <Route path="hardware/requests/:requestId/acceptance-form"
                    element={<ProtectedRoute roles={HW_ALL}><HardwareAcceptanceForm /></ProtectedRoute>} />
                  <Route path="hardware/acceptance-document/:documentId"
                    element={<ProtectedRoute roles={HW_ALL}><HardwareAcceptanceForm /></ProtectedRoute>} />
                  <Route path="my-asset-forms"
                    element={<ProtectedRoute roles={ALL}><MyAssetForms /></ProtectedRoute>} />
                  <Route path="asset-return-on-behalf"
                    element={<ProtectedRoute deptGate={isHrDepartmentUser}><AssetReturnOnBehalf /></ProtectedRoute>} />
                  <Route path="hardware/fulfilment"
                    element={<ProtectedRoute roles={HW_ICT}><HardwareFulfilmentQueue /></ProtectedRoute>} />
                  <Route path="hardware/dashboard"
                    element={<ProtectedRoute roles={HW_ICT}><HardwareDashboard /></ProtectedRoute>} />
                  <Route path="hardware/manager-approvals"
                    element={<ProtectedRoute roles={MGMT}><HardwareManagerApprovals /></ProtectedRoute>} />
                  <Route path="hardware/procurement"
                    element={<ProtectedRoute roles={HW_ICT}><HardwareProcurement /></ProtectedRoute>} />
                  <Route path="hardware/reports"
                    element={<ProtectedRoute roles={HW_ICT}><HardwareReports /></ProtectedRoute>} />

                  {/* ── Software Asset Management ── */}
                  <Route path="software/catalog"
                    element={<ProtectedRoute roles={SW_ALL}><SoftwareCatalog /></ProtectedRoute>} />
                  <Route path="software/my-licenses"
                    element={<ProtectedRoute roles={SW_ALL}><MySoftwareLicenses /></ProtectedRoute>} />
                  <Route path="software/requests"
                    element={<ProtectedRoute roles={SW_ALL}><SoftwareRequests /></ProtectedRoute>} />
                  <Route path="software/licenses"
                    element={<ProtectedRoute roles={SW_ICT}><SoftwareLicenses /></ProtectedRoute>} />
                  <Route path="software/dashboard"
                    element={<ProtectedRoute roles={SW_ICT}><SoftwareDashboard /></ProtectedRoute>} />
                  <Route path="software/sam"
                    element={<ProtectedRoute roles={SW_ICT}><SAMDashboard /></ProtectedRoute>} />
                  <Route path="software/assignments"
                    element={<ProtectedRoute roles={SW_ICT}><SAMAssignments /></ProtectedRoute>} />
                  <Route path="software/sam-reports"
                    element={<ProtectedRoute roles={SW_ICT}><SAMReports /></ProtectedRoute>} />

                  {/* ── Problem Management ── */}
                  <Route path="problem-dashboard"
                    element={<ProtectedRoute roles={PROB_MGT}><ProblemDashboard /></ProtectedRoute>} />
                  <Route path="problems"
                    element={<ProtectedRoute roles={PROB_MGT}><Problems /></ProtectedRoute>} />
                  <Route path="problems/:id"
                    element={<ProtectedRoute roles={PROB_MGT}><ProblemDetail /></ProtectedRoute>} />
                  <Route path="known-errors"
                    element={<ProtectedRoute roles={PROB_MGT}><KnownErrors /></ProtectedRoute>} />

                  {/* ── Knowledge Base ── */}
                  <Route path="knowledge"
                    element={<ProtectedRoute><KnowledgeDashboard /></ProtectedRoute>} />
                  <Route path="knowledge/articles"
                    element={<ProtectedRoute><KnowledgeList /></ProtectedRoute>} />
                  <Route path="knowledge/articles/new"
                    element={<ProtectedRoute roles={KM_WRITE}><KnowledgeEditor /></ProtectedRoute>} />
                  <Route path="knowledge/articles/:id"
                    element={<ProtectedRoute><KnowledgeDetail /></ProtectedRoute>} />
                  <Route path="knowledge/articles/:id/edit"
                    element={<ProtectedRoute roles={KM_WRITE}><KnowledgeEditor /></ProtectedRoute>} />
                  <Route path="knowledge/approval"
                    element={<ProtectedRoute roles={KM_APPROVAL}><KnowledgeApproval /></ProtectedRoute>} />
                  <Route path="knowledge/categories"
                    element={<ProtectedRoute roles={KM_WRITE}><KnowledgeCategories /></ProtectedRoute>} />

                  {/* ── Assets ── */}
                  <Route path="assets/management-dashboard"
                    element={<ProtectedRoute roles={STAFF}><AssetManagementDashboard /></ProtectedRoute>} />
                  <Route path="assets/reports"
                    element={<ProtectedRoute roles={STAFF}><AssetReports /></ProtectedRoute>} />
                  <Route path="assets/:id"
                    element={<ProtectedRoute roles={ALL}><AssetWorkspace /></ProtectedRoute>} />
                  <Route path="assets"          element={<Assets />} />

                  {/* ── Remote Desktop Session Management ── */}
                  <Route path="remote-desktop/:sessionId"
                    element={<ProtectedRoute roles={STAFF} permission="remote_desktop_access"><RemoteDesktopViewer /></ProtectedRoute>} />
                  <Route path="remote-desktop-history"
                    element={<ProtectedRoute roles={STAFF} permission="remote_desktop_access"><SessionHistory /></ProtectedRoute>} />
                  <Route path="download-agent"
                    element={<DownloadAgent />} />
                  <Route path="deploy-wallpaper"
                    element={<ProtectedRoute roles={['admin', 'ict_manager']}><WallpaperDeploy /></ProtectedRoute>} />
                  <Route path="wallpaper-schedules"
                    element={<ProtectedRoute roles={['admin', 'ict_manager']}><WallpaperSchedulePage /></ProtectedRoute>} />
                  <Route path="agent-config-password"
                    element={<ProtectedRoute roles={['admin']}><AgentConfigPassword /></ProtectedRoute>} />

                  <Route path="infra-deployments"
                    element={<ProtectedRoute roles={STAFF}><InfraDeployments /></ProtectedRoute>} />
                  <Route path="maintenance"
                    element={<ProtectedRoute roles={STAFF}><Maintenance /></ProtectedRoute>} />
                  <Route path="maintenance-schedule"
                    element={<ProtectedRoute roles={ALL}><EmployeeMaintenanceSchedule /></ProtectedRoute>} />
                  <Route path="backups"
                    element={<ProtectedRoute roles={STAFF}><Backups /></ProtectedRoute>} />
                  <Route path="vendors"
                    element={<ProtectedRoute roles={STAFF}><VendorHub><Vendors /></VendorHub></ProtectedRoute>} />
                  <Route path="vendors/contracts"
                    element={<ProtectedRoute roles={STAFF}><VendorHub><VendorContracts /></VendorHub></ProtectedRoute>} />
                  <Route path="vendors/governance"
                    element={<ProtectedRoute roles={ICT}><VendorHub><VendorGovernanceDashboard /></VendorHub></ProtectedRoute>} />
                  <Route path="vendors/lifecycle"
                    element={<ProtectedRoute roles={STAFF}><VendorHub><VendorLifecycleDashboard /></VendorHub></ProtectedRoute>} />
                  <Route path="vendors/onboarding"
                    element={<ProtectedRoute roles={STAFF}><VendorHub><VendorOnboarding /></VendorHub></ProtectedRoute>} />
                  <Route path="vendors/certifications"
                    element={<ProtectedRoute roles={STAFF}><VendorHub><VendorCertifications /></VendorHub></ProtectedRoute>} />
                  <Route path="vendors/communications"
                    element={<ProtectedRoute roles={STAFF}><VendorHub><VendorCommunications /></VendorHub></ProtectedRoute>} />
                  <Route path="vendors/sla-breaches"
                    element={<ProtectedRoute roles={STAFF}><VendorHub><VendorSLABreaches /></VendorHub></ProtectedRoute>} />
                  <Route path="vendors/invoices"
                    element={<ProtectedRoute roles={STAFF}><VendorHub><VendorInvoices /></VendorHub></ProtectedRoute>} />
                  <Route path="vendors/spend"
                    element={<ProtectedRoute roles={STAFF}><VendorHub><VendorSpendAnalysis /></VendorHub></ProtectedRoute>} />
                  <Route path="vendors/comparison"
                    element={<ProtectedRoute roles={STAFF}><VendorHub><VendorComparison /></VendorHub></ProtectedRoute>} />
                  <Route path="vendors/risk"
                    element={<ProtectedRoute roles={STAFF}><VendorHub><VendorRiskOverview /></VendorHub></ProtectedRoute>} />
                  <Route path="vendors/scheduler-settings"
                    element={<ProtectedRoute roles={ICT}><VendorHub><VendorSchedulerSettings /></VendorHub></ProtectedRoute>} />

                  {/* ── Financial Governance ── */}
                  <Route path="finance"
                    element={<ProtectedRoute roles={[...ICT, 'sr_engineer', 'employee_manager', 'project_manager', 'delivery_manager']}><FinanceDashboard /></ProtectedRoute>} />
                  <Route path="finance/budgets"
                    element={<ProtectedRoute roles={ICT}><BudgetManagement /></ProtectedRoute>} />
                  <Route path="finance/service-cost-tracking"
                    element={<ProtectedRoute roles={ICT}><ServiceCostTracking /></ProtectedRoute>} />

                  {/* ── Service Portal ── */}
                  <Route path="service-portal"
                    element={<ProtectedRoute><ServicePortal /></ProtectedRoute>} />

                  {/* ── Enterprise Location Management ── */}
                  <Route path="locations"
                    element={<ProtectedRoute roles={STAFF}><LocationDashboard /></ProtectedRoute>} />
                  <Route path="locations/tree"
                    element={<ProtectedRoute roles={STAFF}><LocationTree /></ProtectedRoute>} />
                  <Route path="locations/list"
                    element={<ProtectedRoute roles={STAFF}><LocationList /></ProtectedRoute>} />
                  <Route path="locations/:id"
                    element={<ProtectedRoute roles={STAFF}><LocationDetail /></ProtectedRoute>} />
                  <Route path="locations/reports"
                    element={<ProtectedRoute roles={STAFF}><LocationReports /></ProtectedRoute>} />
                  <Route path="locations/import"
                    element={<ProtectedRoute roles={ICT}><LocationImport /></ProtectedRoute>} />
                  <Route path="locations/settings"
                    element={<ProtectedRoute roles={ICT}><LocationSettings /></ProtectedRoute>} />

                  {/* ── Warehouse Management ── */}
                  <Route path="warehouse"
                    element={<ProtectedRoute roles={STAFF}><WarehouseDashboard /></ProtectedRoute>} />
                  <Route path="warehouse/list"
                    element={<ProtectedRoute roles={STAFF}><WarehouseList /></ProtectedRoute>} />
                  <Route path="warehouse/stock"
                    element={<ProtectedRoute roles={STAFF}><StockExplorer /></ProtectedRoute>} />
                  <Route path="warehouse/goods-receipt"
                    element={<ProtectedRoute roles={STAFF}><GoodsReceiptPage /></ProtectedRoute>} />
                  <Route path="warehouse/operations"
                    element={<ProtectedRoute roles={STAFF}><WarehouseOperations /></ProtectedRoute>} />
                  <Route path="warehouse/reports"
                    element={<ProtectedRoute roles={STAFF}><WarehouseReports /></ProtectedRoute>} />
                  <Route path="warehouse/import"
                    element={<ProtectedRoute roles={ICT}><WarehouseImport /></ProtectedRoute>} />

                  {/* ── Knowledge Document Repository ── */}
                  <Route path="repository/documents"
                    element={<ProtectedRoute roles={ALL}><DocumentLibrary /></ProtectedRoute>} />
                  <Route path="repository/dashboard"
                    element={<ProtectedRoute roles={STAFF}><DocumentDashboard /></ProtectedRoute>} />

                  {/* ── Repair Management ── */}
                  <Route path="repairs"
                    element={<ProtectedRoute roles={STAFF}><RepairDashboard /></ProtectedRoute>} />

                  {/* ── Asset Allocation Intelligence (Sprint 3) ── */}
                  <Route path="operations-hub" element={<ProtectedRoute roles={STAFF}><OperationsHub /></ProtectedRoute>} />
                  <Route path="allocation-center" element={<ProtectedRoute roles={STAFF}><AllocationCenter /></ProtectedRoute>} />
                  <Route path="employee-portfolio" element={<ProtectedRoute roles={STAFF}><EmployeePortfolio /></ProtectedRoute>} />
                  <Route path="employee-portfolio/:employeeId" element={<ProtectedRoute roles={STAFF}><EmployeePortfolio /></ProtectedRoute>} />
                  <Route path="loan-center" element={<ProtectedRoute roles={STAFF}><LoanCenter /></ProtectedRoute>} />
                  <Route path="transfer-center" element={<ProtectedRoute roles={STAFF}><TransferCenter /></ProtectedRoute>} />
                  <Route path="return-center" element={<ProtectedRoute roles={STAFF}><ReturnCenter /></ProtectedRoute>} />
                  <Route path="damage-recovery" element={<ProtectedRoute><DamageRecovery /></ProtectedRoute>} />
                  <Route path="damage-recovery/:id" element={<ProtectedRoute><DamageRecovery /></ProtectedRoute>} />
                  <Route path="reservation-center" element={<ProtectedRoute roles={STAFF}><ReservationCenter /></ProtectedRoute>} />
                  <Route path="inventory-intelligence" element={<ProtectedRoute roles={STAFF}><InventoryIntelligence /></ProtectedRoute>} />
                  <Route path="bulk-operations" element={<ProtectedRoute roles={STAFF} permission="asset_bulk_operations"><BulkOperations /></ProtectedRoute>} />
                  <Route path="qr-scanner" element={<ProtectedRoute roles={STAFF} permission="asset_qr_scan"><QRScanner /></ProtectedRoute>} />

                  {/* ── Asset Configuration & Component Management (Sprint 4) ── */}
                  <Route path="configuration-dashboard" element={<ProtectedRoute roles={STAFF}><ConfigurationDashboard /></ProtectedRoute>} />
                  <Route path="components" element={<ProtectedRoute roles={STAFF}><ComponentRegistry /></ProtectedRoute>} />
                  <Route path="components/:componentId" element={<ProtectedRoute roles={STAFF}><ComponentWorkspace /></ProtectedRoute>} />
                  <Route path="configuration-baselines" element={<ProtectedRoute roles={ICT}><ConfigurationBaselines /></ProtectedRoute>} />

                  {/* ── Asset Intelligence & Financial Management (Sprint 5) ── */}
                  <Route path="asset-intelligence" element={<ProtectedRoute roles={STAFF}><AssetIntelligenceDashboard /></ProtectedRoute>} />
                  <Route path="asset-intelligence/financial" element={<ProtectedRoute roles={ICT}><AssetFinancialManagement /></ProtectedRoute>} />
                  <Route path="asset-intelligence/financial/:assetId" element={<ProtectedRoute roles={ICT}><FinancialWorkspace /></ProtectedRoute>} />
                  <Route path="asset-intelligence/budget" element={<ProtectedRoute roles={ICT}><BudgetForecastPage /></ProtectedRoute>} />
                  <Route path="asset-intelligence/replacement" element={<ProtectedRoute roles={ICT}><ReplacementPlanner /></ProtectedRoute>} />
                  <Route path="asset-intelligence/vendors" element={<ProtectedRoute roles={ICT}><VendorScorecards /></ProtectedRoute>} />

                  {/* ── Enterprise Maintenance Management ── */}
                  <Route path="maintenance-mgmt"
                    element={<ProtectedRoute roles={STAFF}><MaintenanceMgmtDashboard /></ProtectedRoute>} />
                  <Route path="maintenance-mgmt/list"
                    element={<ProtectedRoute roles={STAFF}><MaintenanceMgmtList /></ProtectedRoute>} />
                  <Route path="maintenance-mgmt/new"
                    element={<ProtectedRoute roles={STAFF}><MaintenanceMgmtForm /></ProtectedRoute>} />
                  <Route path="maintenance-mgmt/calendar"
                    element={<ProtectedRoute roles={STAFF}><MaintenanceMgmtCalendar /></ProtectedRoute>} />
                  <Route path="maintenance-mgmt/reports"
                    element={<ProtectedRoute roles={STAFF}><MaintenanceMgmtReports /></ProtectedRoute>} />
                  <Route path="maintenance-mgmt/:id"
                    element={<ProtectedRoute roles={STAFF}><MaintenanceMgmtDetail /></ProtectedRoute>} />
                  <Route path="maintenance-mgmt/:id/work-order"
                    element={<ProtectedRoute roles={STAFF}><MaintenanceMgmtWorkOrder /></ProtectedRoute>} />

                  {/* ── CMDB ── */}
                  <Route path="cmdb"
                    element={<ProtectedRoute roles={CMDB_ALL}><CmdbEnterpriseDashboard /></ProtectedRoute>} />
                  <Route path="cmdb/admin"
                    element={<ProtectedRoute roles={CMDB_MGT}><CmdbAdmin /></ProtectedRoute>} />
                  <Route path="cmdb/governance"
                    element={<ProtectedRoute roles={CMDB_ALL}><CmdbGovernanceDashboard /></ProtectedRoute>} />
                  <Route path="cmdb/dependencies"
                    element={<ProtectedRoute roles={CMDB_ALL}><CmdbDependencyExplorer /></ProtectedRoute>} />
                  <Route path="cmdb/topology"
                    element={<ProtectedRoute roles={CMDB_ALL}><CmdbTopologyViewer /></ProtectedRoute>} />
                  <Route path="cmdb/sync"
                    element={<ProtectedRoute roles={CMDB_MGT}><CmdbSyncDashboard /></ProtectedRoute>} />
                  <Route path="cmdb/operations"
                    element={<ProtectedRoute roles={CMDB_MGT}><CmdbOperationsCenter /></ProtectedRoute>} />
                  <Route path="cmdb/list"
                    element={<ProtectedRoute roles={CMDB_ALL}><CmdbList /></ProtectedRoute>} />
                  <Route path="cmdb/new"
                    element={<ProtectedRoute roles={CMDB_MGT}><CmdbCreateWizard /></ProtectedRoute>} />
                  <Route path="cmdb/reports"
                    element={<ProtectedRoute roles={CMDB_ALL}><CmdbReports /></ProtectedRoute>} />
                  {/* Legacy /cmdb/phase2 — redirects to unified CMDB dashboard */}
                  <Route path="cmdb/phase2"
                    element={<ProtectedRoute roles={CMDB_ALL}><CmdbPhase2Dashboard /></ProtectedRoute>} />
                  <Route path="cmdb/services"
                    element={<ProtectedRoute roles={CMDB_ALL}><BusinessServices /></ProtectedRoute>} />
                  <Route path="cmdb/services/new"
                    element={<ProtectedRoute roles={CMDB_MGT}><BusinessServiceForm /></ProtectedRoute>} />
                  <Route path="cmdb/services/:id"
                    element={<ProtectedRoute roles={CMDB_ALL}><BusinessServiceDetail /></ProtectedRoute>} />
                  <Route path="cmdb/services/:id/edit"
                    element={<ProtectedRoute roles={CMDB_MGT}><BusinessServiceForm /></ProtectedRoute>} />
                  <Route path="cmdb/relationships"
                    element={<ProtectedRoute roles={CMDB_ALL}><CIRelationships /></ProtectedRoute>} />
                  <Route path="cmdb/impact-analysis"
                    element={<ProtectedRoute roles={CMDB_ALL}><ImpactAnalysis /></ProtectedRoute>} />
                  <Route path="cmdb/:id"
                    element={<ProtectedRoute roles={CMDB_ALL}><CmdbCIWorkspace /></ProtectedRoute>} />
                  <Route path="cmdb/:id/edit"
                    element={<ProtectedRoute roles={CMDB_MGT}><CmdbForm /></ProtectedRoute>} />

                  {/* ── Access Management ── */}
                  <Route path="users"
                    element={<ProtectedRoute roles={USER_MGT}><Users /></ProtectedRoute>} />
                  <Route path="users/new"
                    element={<ProtectedRoute roles={USER_MGT}><Register /></ProtectedRoute>} />
                  <Route path="departments"
                    element={<ProtectedRoute roles={ICT}><Departments /></ProtectedRoute>} />
                  <Route path="contacts"
                    element={<ProtectedRoute roles={ICT}><Contacts /></ProtectedRoute>} />
                  <Route path="roles"
                    element={<ProtectedRoute roles={['admin']}><Roles /></ProtectedRoute>} />
                  <Route path="invite"
                    element={<ProtectedRoute roles={ICT}><InviteUser /></ProtectedRoute>} />
                  <Route path="welcome-kits"
                    element={<ProtectedRoute roles={ICT}><EmployeeWelcomeKit /></ProtectedRoute>} />
                  <Route path="welcome-acceptance"
                    element={<ProtectedRoute roles={ALL}><WelcomeKitAcceptance /></ProtectedRoute>} />

                  {/* ── Administration ── */}
                  <Route path="reports"
                    element={<ProtectedRoute roles={ICT}><Reports /></ProtectedRoute>} />
                  <Route path="my-report"
                    element={<ProtectedRoute roles={[...ENG, 'admin', 'ict_manager']}><EngineerReport /></ProtectedRoute>} />
                  <Route path="engineer-performance"
                    element={<ProtectedRoute roles={[...ENG, 'admin', 'ict_manager', 'dashboard_viewer']}><EngineerPerformance /></ProtectedRoute>} />
                  <Route path="audit-logs"
                    element={<ProtectedRoute roles={['admin']}><AuditLogs /></ProtectedRoute>} />
                  <Route path="system-health"
                    element={<ProtectedRoute roles={ICT}><SystemHealthDashboard /></ProtectedRoute>} />
                  <Route path="smtp"
                    element={<ProtectedRoute roles={['admin']}><SmtpSettings /></ProtectedRoute>} />
                  <Route path="email-preview"
                    element={<ProtectedRoute roles={['admin', 'ict_manager']}><EmailPreview /></ProtectedRoute>} />
                  <Route path="banner-management"
                    element={<ProtectedRoute roles={['admin', 'ict_manager']}><BannerManagement /></ProtectedRoute>} />
                  <Route path="app-version-management"
                    element={<ProtectedRoute roles={['admin', 'ict_manager']}><AppVersionManagement /></ProtectedRoute>} />
                  <Route path="mobile-app-report"
                    element={<ProtectedRoute roles={['admin', 'ict_manager']}><MobileAppReport /></ProtectedRoute>} />
                  <Route path="org-settings"
                    element={<ProtectedRoute roles={['admin']}><OrgSettingsPage /></ProtectedRoute>} />
                  <Route path="sla-settings"
                    element={<ProtectedRoute roles={['admin']}><SLASettings /></ProtectedRoute>} />
                  <Route path="theme-settings"
                    element={<ProtectedRoute><ThemeSettings /></ProtectedRoute>} />
                  <Route path="ticket-categories"
                    element={<ProtectedRoute roles={['admin', 'ict_manager']}><TicketCategories /></ProtectedRoute>} />
                  <Route path="vendor-service-types"
                    element={<ProtectedRoute roles={['admin', 'ict_manager']}><VendorServiceTypes /></ProtectedRoute>} />
                  <Route path="asset-categories"
                    element={<ProtectedRoute roles={['admin', 'ict_manager']}><AssetCategorySettings /></ProtectedRoute>} />
                  <Route path="oem-settings"
                    element={<ProtectedRoute roles={['admin', 'ict_manager']}><OEMSettings /></ProtectedRoute>} />
                  <Route path="projects"
                    element={<ProtectedRoute roles={['admin', 'ict_manager', 'project_manager', 'delivery_manager']}><Projects /></ProtectedRoute>} />

                  {/* ── User / General ── */}
                  <Route path="profile"       element={<Profile />} />
                  <Route path="notifications" element={<Notifications />} />
                  <Route path="help"          element={<Help />} />
                  <Route path="download-app"  element={<DownloadApp />} />
                  <Route path="release-notes" element={<ReleaseNotes />} />
                </Route>

                <Route path="/unauthorized" element={<ProtectedRoute><Unauthorized /></ProtectedRoute>} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
            </ChunkErrorBoundary>
          </BrowserRouter>
          </OrgSettingsProvider>
        </NotificationProvider>
      </AuthProvider>
      </ToastProvider>
    </ThemeEngine>
  );
}
